import { useRef, useEffect, useCallback } from 'react';
import { toast as toastLib } from 'react-toastify';

/**
 * Returns toast methods that no-op after the component unmounts.
 * Use this when calling toast() from async callbacks (e.g. after fetch) to avoid
 * "Failed to execute 'removeChild' on 'Node'" when the component has already unmounted.
 * All calls are state-driven: only invoked when mounted.
 */
export function useSafeToast() {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safe = useCallback((fn) => {
    return (...args) => {
      if (isMountedRef.current) {
        fn(...args);
      }
    };
  }, []);

  return {
    success: safe(toastLib.success),
    error: safe(toastLib.error),
    info: safe(toastLib.info),
    warning: safe(toastLib.warning),
    promise: safe(toastLib.promise),
    dismiss: safe(toastLib.dismiss),
    update: safe(toastLib.update),
    isActive: toastLib.isActive,
  };
}

export default useSafeToast;
